import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Pagination, ToggleSwitch, TextInput } from 'flowbite-react';
import { Change, Project, User } from '../../models';
import {
    RootState,
    claimFromTriage,
    getTriageChanges,
    loadChange,
    loadProjects,
    loadUsers,
    resetChanges,
    updateChange,
    useAppDispatch,
    useAppSelector
} from '../../redux';
import { Changes } from '../../services';
import {
    Ability, ChangeModal, Icon, Loader, Select, Button, Alert,
    TriageListView, TriageTableSkeleton, MergeChangesModal,
} from '../../components';
import TriageModifyModal from './TriageModifyModal';
import TriageGridCard from './TriageGridCard';
import type { Value } from '../../components';
import {
    TRIAGE_AUTO_REFRESH_MS,
    TRIAGE_PAGE_SIZE_OPTIONS,
    TriagePageSize,
    TriageViewPrefs,
    formatTriageDocumentTitle,
    loadTriageViewPrefs,
    saveTriageViewPrefs,
} from '../../utils/triageViewPrefs';
import { sortTriageRows } from '../../utils/sortTriageRows';
import { htmlToPlainPreview } from '../../utils/htmlToPlainPreview';
import { matchTriageTextFilters } from '../../utils/triageTextFilters';
import { classifySpamSource, type SpamSourceKind } from '../../utils/spamSource';

/**
 * Triage view: unassigned changes (optionally limited to the email-intake default project),
 * priority buckets, filters, bulk actions, and `ChangeModal` without full-page navigation.
 */

const STATUS_SPAM_SUSPECT = 'spam_suspect';

const canClaimFromTriage = (item: Change) =>
    item.progress?.status === Change.STATUS_PENDING_TRIAGE;

const isTriageQueueItem = (item: Change) =>
    item.progress?.status === Change.STATUS_PENDING_TRIAGE
    || item.progress?.status === Change.STATUS_SPAM_SUSPECT;
const TYPE_DOMAIN_DEV = 'dev';
const TYPE_DOMAIN_NON_DEV = 'non_dev';
const NON_DEV_SUBTYPES = ['support', 'sales', 'oncall', 'abuse', 'ops', 'other'];
const DEV_SUBTYPES = ['fix', 'feature', 'ui', 'i18n', 'refactor', 'test', 'doc', 'lint', 'build', 'release', 'chore'];

type Group = {
    id: 'p1' | 'p2' | 'p3' | 'p4';
    min: number;
    titleKey: string;
    descriptionKey: string;
};

/** Priority bands (min score inclusive) for grouping cards into P1–P4 sections. */
const GROUPS: Group[] = [
    { id: 'p1', min: 100, titleKey: 'priorities.groups.p1', descriptionKey: 'priorities.groups.p1_desc' },
    { id: 'p2', min: 75,  titleKey: 'priorities.groups.p2', descriptionKey: 'priorities.groups.p2_desc' },
    { id: 'p3', min: 50,  titleKey: 'priorities.groups.p3', descriptionKey: 'priorities.groups.p3_desc' },
    { id: 'p4', min: 0,   titleKey: 'priorities.groups.p4', descriptionKey: 'priorities.groups.p4_desc' }
];

/** Derives UI domain (`dev` vs `non_dev`) from legacy `Change.type`. */
const inferDomainFromType = (type?: string) => {
    if (!type || type === Change.TYPE_NONE)
        return TYPE_DOMAIN_NON_DEV;
    if (DEV_SUBTYPES.includes(type))
        return TYPE_DOMAIN_DEV;
    return TYPE_DOMAIN_NON_DEV;
};

/** Subtype for filters / icons: prefers `typeSubtype`, then `type`, then `other`. */
const inferSubtypeFromChange = (item: Change) => item.typeSubtype || item.type || 'other';

/** Persists domain + subtype as legacy `Change.type` (dev subtype or `none`). */
const mapSubtypeToLegacyType = (domain: string, subtype: string) => {
    if (domain === TYPE_DOMAIN_DEV)
        return DEV_SUBTYPES.includes(subtype) ? subtype : Change.TYPE_CHORE;
    return Change.TYPE_NONE;
};

/** Main triage screen: lists, bulk operations, and quick change preview modal. */
function TriageView() {
    const { t, i18n } = useTranslation();
    const dispatch    = useAppDispatch();

    const [projectFilter, setProjectFilter]   = useState<Value>('all');
    const [typeFilter, setTypeFilter]         = useState<Value>('all');
    const [domainFilter, setDomainFilter]     = useState<Value>('all');
    const [subtypeFilter, setSubtypeFilter]   = useState<Value>('all');
    const [requesterFilter, setRequesterFilter] = useState('');
    const [titleFilter, setTitleFilter]       = useState('');
    const [ticketNumberFilter, setTicketNumberFilter] = useState('');
    const [showSpam, setShowSpam]             = useState(false);
    const [spamSourceFilter, setSpamSourceFilter] = useState<Value>('all');
    const [showTrash, setShowTrash]           = useState(false);
    const [filtersOpen, setFiltersOpen]       = useState(false);
    const [selectedChangeIds, setSelectedChangeIds] = useState<string[]>([]);
    const [processingBulk, setProcessingBulk] = useState(false);
    const [bulkFeedback, setBulkFeedback] = useState<{
        color: 'success' | 'failure' | 'warning';
        title: string;
    } | null>(null);
    const [modifyModalOpen, setModifyModalOpen] = useState(false);
    const [modifyTargetIds, setModifyTargetIds] = useState<string[]>([]);
    const [mergeModalOpen, setMergeModalOpen] = useState(false);
    const [mergeTargetIds, setMergeTargetIds] = useState<string[]>([]);
    const [modalChangeId, setModalChangeId]   = useState<string | undefined>();
    // Optimistic local overrides so icons stay highlighted immediately after dispatch
    const [localTypeOverrides, setLocalTypeOverrides] = useState<Record<string, { domain: string; subtype: string }>>({});
    const [viewPrefs, setViewPrefs] = useState<TriageViewPrefs>(() => loadTriageViewPrefs());
    const [spamChanges, setSpamChanges] = useState<Change[]>([]);
    const [trashChanges, setTrashChanges] = useState<Change[]>([]);
    const [page, setPage] = useState(1);

    const updateViewPrefs = (next: TriageViewPrefs) => {
        setViewPrefs(next);
        saveTriageViewPrefs(next);
    };

    const { user }              = useAppSelector((state: RootState) => state.auth);
    const { users }             = useAppSelector((state: RootState) => state.users);
    const { triageChanges, triageChangesStatus, change: detailChange } = useAppSelector((state: RootState) => state.changes);
    const { setting }           = useAppSelector((state: RootState) => state.settings);
    const { projects: allProjects } = useAppSelector((state: RootState) => state.projects);
    const isClient = user?.role === User.ROLE_CLIENT;
    const intakeProjectId = setting?.email_intake?.default_project;

    const reloadTriage = () => {
        dispatch(getTriageChanges(intakeProjectId ? { project: intakeProjectId } : {}));
    };

    const reloadSpam = () => {
        if (!showSpam) {
            setSpamChanges([]);
            return;
        }
        Changes.list({
            'progress.status': STATUS_SPAM_SUSPECT,
            view: 'triage',
            ...(intakeProjectId ? { project: intakeProjectId } : {}),
        })
            .then((data: AnyObj) => {
                setSpamChanges((data.changes || []).map((item: AnyObj) => new Change(item)));
            })
            .catch(() => setSpamChanges([]));
    };

    const reloadTrash = () => {
        if (!showTrash) {
            setTrashChanges([]);
            return;
        }
        Changes.list({
            'progress.status': Change.STATUS_DISCARDED,
            view: 'triage',
            ...(intakeProjectId ? { project: intakeProjectId } : {}),
        })
            .then((data: AnyObj) => {
                setTrashChanges((data.changes || []).map((item: AnyObj) => new Change(item)));
            })
            .catch(() => setTrashChanges([]));
    };

    const reloadAll = () => {
        reloadTriage();
        reloadSpam();
        reloadTrash();
    };

    const modalChangeIdRef = useRef(modalChangeId);
    modalChangeIdRef.current = modalChangeId;

    useEffect(() => {
        dispatch(loadProjects());
    }, [dispatch]);

    useEffect(() => {
        if (!modifyModalOpen)
            return;
        if (!(users && users.length))
            dispatch(loadUsers());
    }, [modifyModalOpen, dispatch, users]);

    useEffect(() => {
        reloadTriage();
    }, [dispatch, intakeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        reloadSpam();
    }, [showSpam, showTrash, intakeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        reloadTrash();
    }, [showTrash, intakeProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    // When a trash card is restored from the modal, refresh the trash list immediately.
    useEffect(() => {
        if (!showTrash || !detailChange || !modalChangeId)
            return;
        if (detailChange._id !== modalChangeId)
            return;
        if (detailChange.progress?.status === Change.STATUS_DISCARDED)
            return;
        reloadTrash();
        reloadTriage();
    }, [showTrash, modalChangeId, detailChange?._id, detailChange?.progress?.status, detailChange?.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!viewPrefs.autoRefresh)
            return undefined;

        const intervalId = window.setInterval(() => {
            if (modalChangeIdRef.current)
                return;
            reloadAll();
        }, TRIAGE_AUTO_REFRESH_MS);

        return () => window.clearInterval(intervalId);
    }, [viewPrefs.autoRefresh, intakeProjectId, showSpam, showTrash]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const queueCount = showTrash
            ? (trashChanges || []).length
            : showSpam
                ? (spamChanges || []).length
                : (triageChanges || []).length;
        document.title = formatTriageDocumentTitle(queueCount, t('app.triage'));
        return () => {
            document.title = 'Yoda';
        };
    }, [triageChanges, spamChanges, trashChanges, showSpam, showTrash, t, i18n.language]);

    const triageCards = useMemo(() => {
        if (showTrash)
            return trashChanges;
        if (showSpam)
            return spamChanges;
        return triageChanges || [];
    }, [triageChanges, spamChanges, trashChanges, showSpam, showTrash]);

    const selectableProjects = useMemo(() =>
        (allProjects || []).slice().sort((a: Project, b: Project) => a.name.localeCompare(b.name)),
    [allProjects]);

    const selectableTypes = useMemo(() => {
        const rawTypes = triageCards.map((item: Change) => item.type).filter(Boolean) as string[];
        return Array.from(new Set(rawTypes)).sort();
    }, [triageCards]);

    const selectableAssignees = useMemo(() => {
        const byId: Record<string, User> = {};
        (users || []).forEach((item: User) => { byId[item._id] = item; });
        return Object.values(byId)
            .filter((item: User) => item.role !== User.ROLE_CLIENT)
            .sort((a, b) => a.fullname().localeCompare(b.fullname()));
    }, [users]);

    const selectableSubtypes = useMemo<string[]>(() => {
        if (domainFilter === TYPE_DOMAIN_DEV)    return [...DEV_SUBTYPES];
        if (domainFilter === TYPE_DOMAIN_NON_DEV) return [...NON_DEV_SUBTYPES];
        const rawSubtypes = triageCards.map((item: Change) => inferSubtypeFromChange(item)).filter(Boolean) as string[];
        return Array.from(new Set(rawSubtypes)).sort();
    }, [triageCards, domainFilter]);

    const filteredChanges = useMemo(() => {
        const filtered = triageCards
            .filter((item: Change) => !intakeProjectId || item.project?._id === intakeProjectId)
            .filter((item: Change) => showTrash || item.progress?.status !== Change.STATUS_DISCARDED)
            .filter((item: Change) => showTrash || showSpam || item.progress?.status !== STATUS_SPAM_SUSPECT)
            .filter((item: Change) => projectFilter === 'all' || item.project?._id === projectFilter)
            .filter((item: Change) => typeFilter === 'all' || item.type === typeFilter)
            .filter((item: Change) => domainFilter === 'all' || (item.typeDomain || inferDomainFromType(item.type)) === domainFilter)
            .filter((item: Change) => subtypeFilter === 'all' || inferSubtypeFromChange(item) === subtypeFilter)
            .filter((item: Change) => {
                if (!showSpam || spamSourceFilter === 'all')
                    return true;
                const kind = classifySpamSource(item.intake?.spamSource).kind as SpamSourceKind;
                // Allowlist/unknown are not in the filter UI; keep them only on "all".
                return kind === spamSourceFilter;
            })
            .filter((item: Change) => matchTriageTextFilters(item, {
                requester: requesterFilter,
                title: titleFilter,
                ticketNumber: ticketNumberFilter,
            }, i18n.language));

        // List mode: sort the full queue before slicing so page size changes keep the same order.
        // Grid mode: keep priority ordering for P1–P4 buckets.
        if (viewPrefs.mode === 'list')
            return sortTriageRows(filtered, viewPrefs.sort, i18n.language);

        return filtered.sort((a: Change, b: Change) => b.priority - a.priority);
    }, [
        triageCards, showSpam, showTrash, spamSourceFilter, projectFilter, typeFilter, domainFilter, subtypeFilter,
        requesterFilter, titleFilter, ticketNumberFilter,
        intakeProjectId, viewPrefs.mode, viewPrefs.sort, i18n.language,
    ]);

    const pageSize = viewPrefs.pageSize;
    const totalPages = Math.max(1, Math.ceil(filteredChanges.length / pageSize) || 1);

    useEffect(() => {
        setPage(1);
    }, [
        projectFilter, typeFilter, domainFilter, subtypeFilter, showSpam, showTrash, spamSourceFilter,
        requesterFilter, titleFilter, ticketNumberFilter,
        pageSize, viewPrefs.mode, viewPrefs.sort.column, viewPrefs.sort.direction,
    ]);

    useEffect(() => {
        if (page > totalPages)
            setPage(totalPages);
    }, [page, totalPages]);

    const pageChanges = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredChanges.slice(start, start + pageSize);
    }, [filteredChanges, page, pageSize]);

    const groupedChanges = useMemo(() => {
        return GROUPS.map((group, index) => {
            const nextGroupMin = GROUPS[index - 1]?.min;
            const rows = pageChanges.filter((item: Change) => {
                if (nextGroupMin === undefined) return item.priority >= group.min;
                return item.priority >= group.min && item.priority < nextGroupMin;
            });
            return { ...group, rows };
        });
    }, [pageChanges]);

    const summaryPreviews = useMemo(() => {
        const previews: Record<string, string> = {};
        pageChanges.forEach((item: Change) => {
            const rawSummary = (item.summary || item.localizedDescription(i18n.language) || '').trim();
            previews[item._id] = rawSummary.length ? htmlToPlainPreview(rawSummary, 180) : '';
        });
        return previews;
    }, [pageChanges, i18n.language]);

    const selectedCount   = selectedChangeIds.length;
    const allSelected     = pageChanges.length > 0
        && pageChanges.every((item: Change) => selectedChangeIds.includes(item._id));
    const someSelected    = selectedCount > 0 && !allSelected;

    const activeFilterCount = [projectFilter, typeFilter, domainFilter, subtypeFilter]
        .filter(v => v !== 'all').length
        + [requesterFilter, titleFilter, ticketNumberFilter].filter(v => v.trim()).length
        + (showSpam ? 1 : 0) + (showTrash ? 1 : 0)
        + (showSpam && spamSourceFilter !== 'all' ? 1 : 0)
        + (viewPrefs.showEmailHeaders ? 1 : 0);

    const pageRangeFrom = filteredChanges.length === 0 ? 0 : ((page - 1) * pageSize) + 1;
    const pageRangeTo = Math.min(page * pageSize, filteredChanges.length);

    const setPageSize = (nextPageSize: TriagePageSize) => {
        updateViewPrefs({ ...viewPrefs, pageSize: nextPageSize });
    };

    const toggleSelectAll = () => {
        if (allSelected) {
            const pageIds = new Set(pageChanges.map((item: Change) => item._id));
            setSelectedChangeIds(prev => prev.filter(id => !pageIds.has(id)));
            return;
        }
        setSelectedChangeIds(prev => {
            const next = new Set(prev);
            pageChanges.forEach((item: Change) => next.add(item._id));
            return Array.from(next);
        });
    };

    const paginationControls = filteredChanges.length > 0 ? (
        <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <span>{t('priorities.page_size')}</span>
                <select
                    className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm"
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value) as TriagePageSize)}
                >
                    {TRIAGE_PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>{size}</option>
                    ))}
                </select>
            </label>
            <span className="text-sm text-gray-600 dark:text-gray-300">
                {t('priorities.page_range', {
                    from: pageRangeFrom,
                    to: pageRangeTo,
                    total: filteredChanges.length,
                })}
            </span>
            {totalPages > 1 && (
                <Pagination
                    currentPage={page}
                    onPageChange={setPage}
                    showIcons
                    totalPages={totalPages}
                    previousLabel=""
                    nextLabel=""
                />
            )}
        </div>
    ) : null;

    const closeChangeModal = () => {
        setModalChangeId(undefined);
        dispatch(resetChanges(true));
    };

    const onRemoveCard = (_removed: AnyObj) => {
        // Soft-delete is handled inside ChangeModal; refresh lists after close.
        closeChangeModal();
        reloadAll();
    };

    const openModifyModal = (ids: string[]) => {
        if (!ids.length)
            return;
        setModifyTargetIds(ids);
        setModifyModalOpen(true);
    };

    const openMergeModal = (ids: string[]) => {
        if (!ids.length)
            return;
        setMergeTargetIds(ids);
        setMergeModalOpen(true);
    };

    const reportBulkResult = (successCount: number, errorCount: number, requestedCount: number) => {
        if (successCount > 0 && errorCount === 0) {
            setBulkFeedback({
                color: 'success',
                title: t('priorities.bulk_success', { count: successCount }) as string,
            });
            return;
        }
        if (successCount > 0 && errorCount > 0) {
            setBulkFeedback({
                color: 'warning',
                title: t('priorities.bulk_partial', {
                    success: successCount,
                    failed: errorCount,
                }) as string,
            });
            return;
        }
        setBulkFeedback({
            color: 'failure',
            title: t('priorities.bulk_error', { count: requestedCount }) as string,
        });
    };

    const applyModify = async (payload: { assigneeId?: string; projectId?: string }) => {
        if (!payload.assigneeId && !payload.projectId)
            return;
        const requestedCount = modifyTargetIds.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'modify',
                changeIds: modifyTargetIds,
                assignee: payload.assigneeId,
                projectId: payload.projectId,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setModifyModalOpen(false);
            setModifyTargetIds([]);
            setSelectedChangeIds([]);
            reloadAll();
        } catch (err) {
            console.error(err);
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const applyMerge = async (target: Change) => {
        if (!target?._id || !mergeTargetIds.length)
            return;
        const requestedCount = mergeTargetIds.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            await Changes.merge(target._id, mergeTargetIds);
            reportBulkResult(requestedCount, 0, requestedCount);
            setMergeModalOpen(false);
            setMergeTargetIds([]);
            setSelectedChangeIds([]);
            if (modalChangeId && mergeTargetIds.includes(modalChangeId))
                closeChangeModal();
            reloadAll();
        } catch (err) {
            console.error(err);
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const resolveCards = async (ids: string[]) => {
        if (!ids.length)
            return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'resolve',
                changeIds: ids,
                assignee: user?._id,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setSelectedChangeIds([]);
            reloadAll();
        } catch (err) {
            console.error(err);
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const restoreCards = async (ids: string[]) => {
        if (!ids.length)
            return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'restore',
                changeIds: ids,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setSelectedChangeIds([]);
            reloadAll();
        } catch (err) {
            console.error(err);
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    /** spam_suspect → pendingTriage (same semantics as MCP approve_spam_suspect). */
    const markSafeCards = async (ids: string[]) => {
        if (!ids.length)
            return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const results = await Promise.allSettled(
                ids.map((id) => Changes.toStep(id, Change.STATUS_PENDING_TRIAGE))
            );
            const successCount = results.filter((r) => r.status === 'fulfilled').length;
            const errorCount = requestedCount - successCount;
            reportBulkResult(successCount, errorCount, requestedCount);
            setSelectedChangeIds([]);
            if (modalChangeId && ids.includes(modalChangeId))
                closeChangeModal();
            reloadAll();
        } catch (err) {
            console.error(err);
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    const discardCards = async (ids: string[]) => {
        if (!ids.length)
            return;
        const requestedCount = ids.length;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            const result = await Changes.bulkFromTriage({
                action: 'discard',
                changeIds: ids,
            });
            const errorCount = result.errors?.length || 0;
            reportBulkResult(result.changes.length, errorCount, requestedCount);
            setSelectedChangeIds([]);
            if (modalChangeId && ids.includes(modalChangeId))
                closeChangeModal();
            reloadAll();
        } catch (err) {
            console.error(err);
            reportBulkResult(0, requestedCount, requestedCount);
        } finally {
            setProcessingBulk(false);
        }
    };

    // Soft-delete goes to trash — no confirm dialog needed.
    const requestDiscard = (ids: string[]) => {
        discardCards(ids);
    };

    const performBulkModify = () => openModifyModal(selectedChangeIds);

    const performBulkMerge = () => openMergeModal(selectedChangeIds);

    const performBulkResolve = () => resolveCards(selectedChangeIds);

    const performBulkRestore = () => restoreCards(selectedChangeIds);

    const performBulkDiscard = () => requestDiscard(selectedChangeIds);

    const performBulkMarkSafe = () => markSafeCards(selectedChangeIds);

    const handleToggleSelect = useCallback((changeId: string) => {
        setSelectedChangeIds(prev => prev.includes(changeId)
            ? prev.filter(id => id !== changeId)
            : [...prev, changeId]
        );
    }, []);

    const handleOpenChange = useCallback((item: Change) => {
        if (!item.project?._id || !item._id) return;
        setModalChangeId(item._id);
        dispatch(loadChange(item._id));
    }, [dispatch]);

    const handleTake = useCallback((item: Change) => {
        if (!user?._id || !canClaimFromTriage(item) || processingBulk)
            return;
        setBulkFeedback(null);
        setProcessingBulk(true);
        dispatch(claimFromTriage(item._id, user._id, (err: Error | null) => {
            if (err) {
                reportBulkResult(0, 1, 1);
            } else {
                reportBulkResult(1, 0, 1);
                reloadAll();
            }
            setProcessingBulk(false);
        }));
    }, [dispatch, user?._id, processingBulk]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleModify = useCallback((item: Change) => {
        openModifyModal([item._id]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMerge = useCallback(async (item: Change) => {
        const candidateId = item.technicalContext?.mergeReview?.candidateId;
        if (candidateId) {
            if (processingBulk)
                return;
            setProcessingBulk(true);
            try {
                await Changes.merge(candidateId, [item._id]);
                setSelectedChangeIds((prev) => prev.filter((id) => id !== item._id));
                if (modalChangeId && modalChangeId === item._id)
                    closeChangeModal();
                reloadAll();
            } catch (err) {
                console.error(err);
                reportBulkResult(0, 1, 1);
            } finally {
                setProcessingBulk(false);
            }
            return;
        }
        openMergeModal([item._id]);
    }, [modalChangeId, processingBulk]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleResolve = useCallback((item: Change) => {
        resolveCards([item._id]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDiscard = useCallback((item: Change) => {
        requestDiscard([item._id]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRestore = useCallback((item: Change) => {
        restoreCards([item._id]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleMarkSafe = useCallback((item: Change) => {
        markSafeCards([item._id]);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const trustSpamSenderCard = async (item: Change, scope: 'email' | 'domain') => {
        if (!item?._id || processingBulk)
            return;
        setBulkFeedback(null);
        setProcessingBulk(true);
        try {
            await Changes.trustSpamSender(item._id, scope);
            reportBulkResult(1, 0, 1);
            setSelectedChangeIds((prev) => prev.filter((id) => id !== item._id));
            if (modalChangeId && modalChangeId === item._id)
                closeChangeModal();
            reloadAll();
        } catch (err) {
            console.error(err);
            reportBulkResult(0, 1, 1);
        } finally {
            setProcessingBulk(false);
        }
    };

    const handleTrustSender = useCallback((item: Change) => {
        trustSpamSenderCard(item, 'email');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTrustDomain = useCallback((item: Change) => {
        trustSpamSenderCard(item, 'domain');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUpdateType = useCallback((item: Change, domain: string, subtype: string) => {
        setLocalTypeOverrides(prev => ({ ...prev, [item._id]: { domain, subtype } }));
        dispatch(updateChange({
            _id: item._id,
            typeDomain: domain,
            typeSubtype: subtype,
            type: mapSubtypeToLegacyType(domain, subtype)
        }, true));
    }, [dispatch]);

    if (isClient)
        return <Navigate to="/" replace />;

    const isInitialTriageLoad = !showTrash
        && triageChangesStatus === 'pending'
        && !(triageChanges && triageChanges.length);

    const toggleAutoRefresh = () => {
        const next = !viewPrefs.autoRefresh;
        updateViewPrefs({ ...viewPrefs, autoRefresh: next });
        if (next)
            reloadAll();
    };

    return (
        <Ability can="change:read" redirect="/">
            <div className="TriageView">
                <header className="border-b bg-white dark:bg-gray-800 px-4 py-0 sm:px-6 lg:px-8">
                    <div className="mx-auto my-2 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                            <Icon type="priority" size={7} className="mr-1.5 inline" />
                            {t('app.triage')}
                        </h2>
                        <div className="flex items-center gap-2">
                            <div className="inline-flex overflow-hidden rounded-md border border-gray-200 dark:border-gray-700">
                                <button
                                    type="button"
                                    className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm ${
                                        viewPrefs.mode === 'grid'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                    onClick={() => updateViewPrefs({ ...viewPrefs, mode: 'grid' })}
                                >
                                    <Icon type="board" size={4} color={viewPrefs.mode === 'grid' ? 'white' : undefined} />
                                    {t('priorities.view_grid')}
                                </button>
                                <button
                                    type="button"
                                    title={t('priorities.list.density_comfortable') as string}
                                    className={`inline-flex items-center gap-1 border-l border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm ${
                                        viewPrefs.mode === 'list' && viewPrefs.density === 'comfortable'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                    onClick={() => updateViewPrefs({
                                        ...viewPrefs,
                                        mode: 'list',
                                        density: 'comfortable',
                                    })}
                                >
                                    <Icon
                                        type="list"
                                        size={4}
                                        color={viewPrefs.mode === 'list' && viewPrefs.density === 'comfortable' ? 'white' : undefined}
                                    />
                                    {t('priorities.view_list_comfortable')}
                                </button>
                                <button
                                    type="button"
                                    title={t('priorities.list.density_compact') as string}
                                    className={`inline-flex items-center gap-1 border-l border-gray-200 dark:border-gray-700 px-3 py-1.5 text-sm ${
                                        viewPrefs.mode === 'list' && viewPrefs.density === 'compact'
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                    onClick={() => updateViewPrefs({
                                        ...viewPrefs,
                                        mode: 'list',
                                        density: 'compact',
                                    })}
                                >
                                    <Icon
                                        type="menu"
                                        size={4}
                                        color={viewPrefs.mode === 'list' && viewPrefs.density === 'compact' ? 'white' : undefined}
                                    />
                                    {t('priorities.view_list_compact')}
                                </button>
                            </div>
                            <button
                                type="button"
                                aria-pressed={viewPrefs.autoRefresh}
                                title={(viewPrefs.autoRefresh
                                    ? t('priorities.auto_refresh_disable')
                                    : t('priorities.auto_refresh_enable')) as string}
                                className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm ${
                                    viewPrefs.autoRefresh
                                        ? 'border-blue-600 bg-blue-600 text-white'
                                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                }`}
                                onClick={toggleAutoRefresh}
                            >
                                <Icon
                                    type="sync"
                                    size={4}
                                    color={viewPrefs.autoRefresh ? 'white' : undefined}
                                />
                                {t('priorities.auto_refresh')}
                            </button>
                        </div>
                    </div>
                </header>

                <main className="p-5">
                    {/* Collapsible filters */}
                    <div className="mb-5 rounded-lg border bg-white dark:bg-gray-800 p-3 shadow-sm">
                        <div
                            className="flex cursor-pointer items-center justify-between"
                            onClick={() => setFiltersOpen(o => !o)}
                        >
                            <span className="text-sm font-semibold">
                                {t('priorities.filters.title') || 'Filtres'}
                                {activeFilterCount > 0 && (
                                    <span className="ml-1 text-blue-600">({activeFilterCount})</span>
                                )}
                            </span>
                            <Icon type={filtersOpen ? 'up' : 'down'} size={4} />
                        </div>
                        {filtersOpen && (
                            <div className="mt-3 space-y-3">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                    <Select
                                        className="!mt-0"
                                        onChange={setProjectFilter}
                                        defaultValue={projectFilter}
                                        placeholder={t('priorities.filters.project') as string}
                                        options={[
                                            { value: 'all', label: t('priorities.filters.all_projects') || '' },
                                            ...selectableProjects.map((item: Project) => ({ value: item._id, label: item.name }))
                                        ]}
                                    />
                                    <Select
                                        className="!mt-0"
                                        onChange={setTypeFilter}
                                        defaultValue={typeFilter}
                                        placeholder={t('priorities.filters.type') as string}
                                        options={[
                                            { value: 'all', label: t('priorities.filters.all_types') || '' },
                                            ...selectableTypes.map((item: string) => ({ value: item, label: item }))
                                        ]}
                                    />
                                    <Select
                                        className="!mt-0"
                                        onChange={setDomainFilter}
                                        defaultValue={domainFilter}
                                        placeholder={t('priorities.domain') as string}
                                        options={[
                                            { value: 'all', label: t('priorities.all_domains') || '' },
                                            { value: TYPE_DOMAIN_DEV, label: TYPE_DOMAIN_DEV },
                                            { value: TYPE_DOMAIN_NON_DEV, label: TYPE_DOMAIN_NON_DEV }
                                        ]}
                                    />
                                    <Select
                                        className="!mt-0"
                                        onChange={setSubtypeFilter}
                                        defaultValue={subtypeFilter}
                                        placeholder={t('priorities.subtype') as string}
                                        options={[
                                            { value: 'all', label: t('priorities.all_subtypes') || '' },
                                            ...selectableSubtypes.map((item: string) => ({ value: item, label: item }))
                                        ]}
                                    />
                                </div>
                                <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                                    <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-3">
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                                                {t('priorities.filters.requester')}
                                            </label>
                                            <TextInput
                                                type="text"
                                                value={requesterFilter}
                                                onChange={(e) => setRequesterFilter(e.target.value)}
                                                placeholder={t('priorities.filters.requester_placeholder') || ''}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                                                {t('priorities.filters.subject')}
                                            </label>
                                            <TextInput
                                                type="text"
                                                value={titleFilter}
                                                onChange={(e) => setTitleFilter(e.target.value)}
                                                placeholder={t('priorities.filters.subject_placeholder') || ''}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                                                {t('priorities.filters.ticket_number')}
                                            </label>
                                            <TextInput
                                                type="text"
                                                value={ticketNumberFilter}
                                                onChange={(e) => setTicketNumberFilter(e.target.value)}
                                                placeholder={t('priorities.filters.ticket_number_placeholder') || ''}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 px-3 py-2 lg:shrink-0">
                                        <ToggleSwitch
                                            checked={showSpam}
                                            disabled={showTrash}
                                            onChange={(value) => {
                                                setShowSpam(value);
                                                if (value)
                                                    setShowTrash(false);
                                                else
                                                    setSpamSourceFilter('all');
                                            }}
                                            label={(showSpam ? t('priorities.show_spam') : t('priorities.hide_spam')) as string}
                                        />
                                        <ToggleSwitch
                                            checked={showTrash}
                                            onChange={(value) => {
                                                setShowTrash(value);
                                                if (value) {
                                                    setShowSpam(false);
                                                    setSpamSourceFilter('all');
                                                }
                                            }}
                                            label={(showTrash ? t('priorities.show_trash') : t('priorities.hide_trash')) as string}
                                        />
                                        <ToggleSwitch
                                            checked={viewPrefs.showEmailHeaders}
                                            onChange={(value) => updateViewPrefs({
                                                ...viewPrefs,
                                                showEmailHeaders: value,
                                            })}
                                            label={(viewPrefs.showEmailHeaders
                                                ? t('priorities.show_email_headers')
                                                : t('priorities.hide_email_headers')) as string}
                                        />
                                    </div>
                                </div>
                                {showSpam && (
                                    <div className="max-w-xs">
                                        <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                                            {t('priorities.filters.spam_source')}
                                        </label>
                                        <Select
                                            className="!mt-0"
                                            onChange={setSpamSourceFilter}
                                            defaultValue={spamSourceFilter}
                                            placeholder={t('priorities.filters.spam_source') as string}
                                            options={[
                                                { value: 'all', label: t('priorities.filters.all_spam_sources') || '' },
                                                { value: 'ai', label: t('priorities.spam_source.ai') || '' },
                                                { value: 'mta', label: t('priorities.spam_source.mta') || '' },
                                                { value: 'routine', label: t('priorities.spam_source.routine') || '' },
                                            ]}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Bulk actions — only when at least one card is selected */}
                    {selectedCount > 0 && (
                        <div className="mb-3 rounded-lg border bg-white dark:bg-gray-800 p-3 shadow-sm">
                            <div className="mb-0 flex flex-wrap items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {t('priorities.bulk_actions')}
                                    <span className="ml-1 font-normal text-blue-600">({selectedCount})</span>
                                    {processingBulk && (
                                        <span className="ml-2 inline-flex items-center gap-1 font-normal text-gray-500 dark:text-gray-400">
                                            <Icon type="loading" size={4} color="gray-500" />
                                            {t('priorities.bulk_processing')}
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                    {!showTrash && (
                                        <>
                                            {showSpam && (
                                                <Button
                                                    title={t('priorities.bulk_mark_safe') as string}
                                                    icon="thumbs-up"
                                                    small
                                                    color="success"
                                                    onClick={() => { if (!processingBulk) performBulkMarkSafe(); }}
                                                    className={processingBulk ? 'opacity-50' : ''}
                                                />
                                            )}
                                            {!showSpam && (
                                                <>
                                                    <Button
                                                        title={t('priorities.modify') as string}
                                                        icon="edit"
                                                        small
                                                        color="primary"
                                                        onClick={() => { if (!processingBulk) performBulkModify(); }}
                                                        className={processingBulk ? 'opacity-50' : ''}
                                                    />
                                                    <Button
                                                        title={t('priorities.bulk_merge') as string}
                                                        icon="merge"
                                                        small
                                                        color="primary"
                                                        onClick={() => { if (!processingBulk) performBulkMerge(); }}
                                                        className={processingBulk ? 'opacity-50' : ''}
                                                    />
                                                    <Button
                                                        title={t('priorities.resolve') as string}
                                                        icon="check"
                                                        small
                                                        color="success"
                                                        onClick={() => { if (!processingBulk) performBulkResolve(); }}
                                                        className={processingBulk ? 'opacity-50' : ''}
                                                    />
                                                </>
                                            )}
                                            <Button
                                                title={t('priorities.discard') as string}
                                                icon="delete"
                                                small
                                                color="danger"
                                                onClick={() => { if (!processingBulk) performBulkDiscard(); }}
                                                className={processingBulk ? 'opacity-50' : ''}
                                            />
                                        </>
                                    )}
                                    {showTrash && (
                                        <Button
                                            title={t('priorities.bulk_restore') as string}
                                            icon="back"
                                            small
                                            color="primary"
                                            onClick={() => { if (!processingBulk) performBulkRestore(); }}
                                            className={processingBulk ? 'opacity-50' : ''}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {bulkFeedback && (
                        <div className="mb-3">
                            <Alert
                                title={bulkFeedback.title}
                                color={bulkFeedback.color}
                                icon={bulkFeedback.color === 'success' ? 'check' : 'warning'}
                            />
                        </div>
                    )}

                    {/* Card grid / dense list */}
                    {isInitialTriageLoad ? (
                        viewPrefs.mode === 'list' ? (
                            <TriageTableSkeleton
                                rows={Math.min(viewPrefs.pageSize || 10, 12)}
                                density={viewPrefs.density}
                            />
                        ) : (
                            <div
                                className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
                                data-testid="triage-grid-skeleton"
                                aria-busy="true"
                            >
                                {Array.from({ length: 6 }).map((_, index) => (
                                    <div
                                        key={index}
                                        className="animate-pulse rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm"
                                    >
                                        <div className="mb-3 h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
                                        <div className="mb-2 h-3 w-full rounded bg-gray-200 dark:bg-gray-700" />
                                        <div className="mb-4 h-3 w-4/5 rounded bg-gray-200 dark:bg-gray-700" />
                                        <div className="flex gap-2">
                                            <div className="h-7 w-16 rounded bg-gray-200 dark:bg-gray-700" />
                                            <div className="h-7 w-16 rounded bg-gray-200 dark:bg-gray-700" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : viewPrefs.mode === 'list' ? (
                        filteredChanges.length === 0 ? (
                            <div className="rounded border border-dashed border-slate-300 bg-white dark:bg-gray-800 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                {t('priorities.empty_group')}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex flex-wrap items-center justify-end gap-3">
                                    {paginationControls}
                                </div>
                                <TriageListView
                                    rows={pageChanges}
                                    prefs={viewPrefs}
                                    selectedChangeIds={selectedChangeIds}
                                    allSelected={allSelected}
                                    someSelected={someSelected}
                                    onToggleSelectAll={toggleSelectAll}
                                    language={i18n.language}
                                    summaryPreviews={summaryPreviews}
                                    onPrefsChange={updateViewPrefs}
                                    onToggleSelect={handleToggleSelect}
                                    onOpen={handleOpenChange}
                                    onTake={handleTake}
                                    onModify={handleModify}
                                    onMerge={handleMerge}
                                    onResolve={handleResolve}
                                    onDiscard={handleDiscard}
                                    onRestore={handleRestore}
                                    onMarkSafe={handleMarkSafe}
                                    onTrustSender={handleTrustSender}
                                    onTrustDomain={handleTrustDomain}
                                    canTakeCard={canClaimFromTriage}
                                    isTrashView={showTrash}
                                />
                                {totalPages > 1 && (
                                    <div className="flex justify-center">
                                        <Pagination
                                            currentPage={page}
                                            onPageChange={setPage}
                                            showIcons
                                            totalPages={totalPages}
                                            previousLabel=""
                                            nextLabel=""
                                        />
                                    </div>
                                )}
                            </div>
                        )
                    ) : (
                        <div className="space-y-4">
                            {filteredChanges.length > 0 && (
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <label className="flex w-fit cursor-pointer items-center gap-2 rounded-lg border bg-white dark:bg-gray-800 px-3 py-2 text-sm font-medium shadow-sm select-none">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                            onChange={toggleSelectAll}
                                            className="h-4 w-4 cursor-pointer"
                                        />
                                        {t('priorities.select_all')}
                                    </label>
                                    {paginationControls}
                                </div>
                            )}
                            {groupedChanges.filter(group => group.rows.length > 0).map(group => (
                                <section key={group.id} className="rounded-lg border bg-slate-50 dark:bg-gray-800 p-4">
                                    <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-2">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                                                {t(group.titleKey)} ({group.rows.length})
                                            </h3>
                                            <p className="text-xs text-gray-500 dark:text-gray-400">{t(group.descriptionKey)}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                                        {group.rows.map((item: Change) => (
                                            <TriageGridCard
                                                key={item._id}
                                                item={item}
                                                isSelected={selectedChangeIds.includes(item._id)}
                                                summary={summaryPreviews[item._id] || ''}
                                                language={i18n.language}
                                                user={user}
                                                showTrash={showTrash}
                                                typeOverride={localTypeOverrides[item._id]}
                                                canClaim={canClaimFromTriage(item)}
                                                isQueueItem={isTriageQueueItem(item)}
                                                onToggleSelect={handleToggleSelect}
                                                onOpen={handleOpenChange}
                                                onTake={handleTake}
                                                onModify={handleModify}
                                                onMerge={handleMerge}
                                                onResolve={handleResolve}
                                                onDiscard={handleDiscard}
                                                onRestore={handleRestore}
                                                onMarkSafe={handleMarkSafe}
                                                onTrustSender={handleTrustSender}
                                                onTrustDomain={handleTrustDomain}
                                                onUpdateType={handleUpdateType}
                                            />
                                        ))}
                                    </div>
                                </section>
                            ))}
                            {filteredChanges.length > 0 && totalPages > 1 && (
                                <div className="flex justify-center">
                                    <Pagination
                                        currentPage={page}
                                        onPageChange={setPage}
                                        showIcons
                                        totalPages={totalPages}
                                        previousLabel=""
                                        nextLabel=""
                                    />
                                </div>
                            )}
                            {groupedChanges.every(group => group.rows.length === 0) && (
                                <div className="rounded border border-dashed border-slate-300 bg-white dark:bg-gray-800 p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                    {t('priorities.empty_group')}
                                </div>
                            )}
                        </div>
                    )}
                </main>

                {detailChange && detailChange._id === modalChangeId && detailChange.project?.name && (
                    <ChangeModal
                        project={detailChange.project}
                        change={detailChange}
                        show={true}
                        showProjectName={true}
                        onClose={closeChangeModal}
                        onRemove={onRemoveCard}
                    />
                )}

                <TriageModifyModal
                    show={modifyModalOpen}
                    count={modifyTargetIds.length}
                    assignees={selectableAssignees}
                    projects={selectableProjects}
                    processing={processingBulk}
                    onClose={() => {
                        setModifyModalOpen(false);
                        setModifyTargetIds([]);
                    }}
                    onApply={applyModify}
                />

                <MergeChangesModal
                    show={mergeModalOpen}
                    sources={triageCards.filter((item: Change) => mergeTargetIds.includes(item._id))}
                    processing={processingBulk}
                    defaultProjectId={triageCards.find((item: Change) => mergeTargetIds.includes(item._id))?.project?._id}
                    onClose={() => {
                        setMergeModalOpen(false);
                        setMergeTargetIds([]);
                    }}
                    onApply={applyMerge}
                />

                {processingBulk && <Loader />}

            </div>
        </Ability>
    );
}

export default TriageView;
